// src/components/ReadhitComponent/SequenceComponent.js
import React, { useState, useEffect, useCallback } from 'react';
import SequenceAlignmentViz from '../../views/testSequenceAlignment.js';
import UserGuideSequenceAlignment from '../../utils/UserGuid/UserGuideSequenceAlignment.js';

// ─────────────────────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────────────────────

const ITEMS_PER_PAGE = 10;

const SIMILARITY_RANGES = [
  { range: '> 95%',   min: 95,  max: 100, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  { range: '90–95%',  min: 90,  max: 95,  color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  { range: '85–90%',  min: 85,  max: 90,  color: '#0891b2', bg: '#ecfeff', border: '#a5f3fc' },
  { range: '80–85%',  min: 80,  max: 85,  color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  { range: '75–80%',  min: 75,  max: 80,  color: '#64748b', bg: '#f8fafc', border: '#e2e8f0' },
  { range: '< 75%',   min: 0,   max: 75,  color: '#94a3b8', bg: '#f1f5f9', border: '#e2e8f0' },
];

function getSimilarityRange(similarity) {
  const pct = parseFloat(similarity);
  return (
    SIMILARITY_RANGES.find(r => pct > r.min && pct <= r.max) ||
    SIMILARITY_RANGES[SIMILARITY_RANGES.length - 1]
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function SimilarityBadge({ value }) {
  const range = getSimilarityRange(value);
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 7px',
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 700,
      fontFamily: 'var(--rh-mono)',
      background: range.bg,
      color: range.color,
      border: `1px solid ${range.border}`,
      whiteSpace: 'nowrap',
    }}>
      {value.toFixed(2)}%
    </span>
  );
}

function RangeFilterChip({ range, active, count, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        border: `1px solid ${range.border}`,
        background: active ? range.color : range.bg,
        color: active ? '#fff' : range.color,
        transition: 'all 0.12s',
        whiteSpace: 'nowrap',
      }}
    >
      {range.range} ({count})
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────────

const SequenceComponent = ({ data }) => {
  const [selectedAlignment,      setSelectedAlignment]      = useState(null);
  const [selectedAlignmentIndex, setSelectedAlignmentIndex] = useState(null);
  const [currentPage,            setCurrentPage]            = useState(1);
  const [analysisResults,        setAnalysisResults]        = useState([]);
  const [selectedRangeFilter,    setSelectedRangeFilter]    = useState(null);
  const [guideOpen,              setGuideOpen]              = useState(false);

  // ── Select first item on mount ───────────────────────────────────────────
  useEffect(() => {
    if (data?.length && !selectedAlignment) {
      setSelectedAlignment(data[0]);
      setSelectedAlignmentIndex(0);
    }
  }, [data, selectedAlignment]);

  // ── Filtered data ────────────────────────────────────────────────────────
  const filteredData = selectedRangeFilter
    ? data.filter((_, idx) => {
        const result = analysisResults.find(r => r.alignmentIndex === idx);
        if (!result) return false;
        return result.similarity > selectedRangeFilter.min &&
               result.similarity <= selectedRangeFilter.max;
      })
    : (data || []);

  const totalPages  = Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE));
  const safePage    = Math.min(currentPage, totalPages);
  const currentData = filteredData.slice(
    (safePage - 1) * ITEMS_PER_PAGE,
    safePage * ITEMS_PER_PAGE,
  );

  // ── Analysis result tracking ─────────────────────────────────────────────
  const addAnalysisResult = useCallback((alignmentIndex, info) => {
    setAnalysisResults(prev => {
      const idx = prev.findIndex(r => r.alignmentIndex === alignmentIndex);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...info };
        return updated;
      }
      return [...prev, { alignmentIndex, ...info }];
    });
  }, []);

  const handleSimilarityRangeFound = useCallback((matchInfo) => {
    if (selectedAlignmentIndex !== null) {
      addAnalysisResult(selectedAlignmentIndex, {
        similarity: matchInfo.similarity,
        range:      matchInfo.range,
        bestMatch:  matchInfo.strain,
        positions:  [`${matchInfo.position} – ${parseInt(matchInfo.position) + (selectedAlignment?.qseq?.length || 0)}`],
      });
    }
  }, [selectedAlignmentIndex, selectedAlignment?.qseq?.length, addAnalysisResult]);

  // ── Range counts ─────────────────────────────────────────────────────────
  const rangeCounts = SIMILARITY_RANGES.reduce((acc, r) => {
    acc[r.range] = analysisResults.filter(
      res => res.similarity > r.min && res.similarity <= r.max
    ).length;
    return acc;
  }, {});

  const activeRanges = SIMILARITY_RANGES.filter(r => rangeCounts[r.range] > 0);

  // ── Toggle range filter ───────────────────────────────────────────────────
  const toggleRangeFilter = (range) => {
    setSelectedRangeFilter(prev =>
      prev && prev.range === range.range ? null : range
    );
    setCurrentPage(1);
  };

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div style={{ display: 'flex', height: '100%', gap: 0, overflow: 'hidden' }}>

        {/* ── Left panel — alignment list ── */}
        <div style={{
          width: 300,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--rh-surface)',
          borderRight: '1px solid var(--rh-border)',
          overflow: 'hidden',
        }}>

          {/* Panel header */}
          <div style={{
            padding: '10px 14px',
            background: 'var(--rh-topbar-bg)',
            borderBottom: '1px solid rgba(255,255,255,.08)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#f1f5f9', fontFamily: 'var(--rh-mono)' }}>
                  Sequence Alignments
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                  {data?.length ?? 0} total alignments
                </div>
              </div>
              <button className="rh-btn-reset"
                style={{ background: 'rgba(255,255,255,.08)', borderColor: 'rgba(255,255,255,.18)', color: '#cbd5e1' }}
                onClick={() => setGuideOpen(true)}>
                ? Guide
              </button>
            </div>

            {/* Range filter chips */}
            {activeRanges.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#475569', marginBottom: 6 }}>
                  Filter by Similarity · {analysisResults.length} analyzed
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {activeRanges.map(r => (
                    <RangeFilterChip
                      key={r.range}
                      range={r}
                      active={selectedRangeFilter?.range === r.range}
                      count={rangeCounts[r.range]}
                      onClick={() => toggleRangeFilter(r)}
                    />
                  ))}
                  {selectedRangeFilter && (
                    <button
                      onClick={() => { setSelectedRangeFilter(null); setCurrentPage(1); }}
                      style={{
                        padding: '2px 8px', borderRadius: 999, fontSize: 11,
                        fontWeight: 600, cursor: 'pointer',
                        border: '1px solid var(--rh-border-dark)',
                        background: 'var(--rh-bg)', color: 'var(--rh-text-muted)',
                      }}>
                      ✕ Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Alignment list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {!data?.length ? (
              <div className="rh-empty">
                <div className="rh-empty-icon">◎</div>
                <span>No sequence alignment data available.</span>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="rh-empty">
                <div className="rh-empty-icon">◎</div>
                <span>No alignments match the selected filter.</span>
                <button className="rh-btn-reset" onClick={() => setSelectedRangeFilter(null)}>
                  Clear Filter
                </button>
              </div>
            ) : (
              currentData.map((alignment) => {
                const fullIdx = data.indexOf(alignment);
                const result  = analysisResults.find(r => r.alignmentIndex === fullIdx);
                const isActive = selectedAlignment === alignment;

                return (
                  <button
                    key={fullIdx}
                    onClick={() => { setSelectedAlignment(alignment); setSelectedAlignmentIndex(fullIdx); }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '9px 14px',
                      textAlign: 'left',
                      border: 'none',
                      borderBottom: '1px solid var(--rh-border)',
                      background: isActive ? 'var(--rh-accent-light)' : 'transparent',
                      borderLeft: `3px solid ${isActive ? 'var(--rh-accent)' : 'transparent'}`,
                      cursor: 'pointer',
                      transition: 'background 0.1s',
                    }}
                  >
                    {/* Title row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--rh-text)' }}>
                        Alignment {fullIdx + 1}
                      </span>
                      {result && <SimilarityBadge value={result.similarity} />}
                    </div>

                    {/* Query / Subject */}
                    <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', fontFamily: 'var(--rh-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>Q:</strong> {alignment.qseqid || 'N/A'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--rh-text-muted)', fontFamily: 'var(--rh-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>S:</strong> {alignment.sseqid || 'N/A'}
                    </div>

                    {/* Analysis result details */}
                    {result && (
                      <div style={{ marginTop: 5, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        <span style={{ fontSize: 10, color: 'var(--rh-text-muted)' }}>
                          Range: <strong>{getSimilarityRange(result.similarity).range}</strong>
                        </span>
                        {result.bestMatch && (
                          <span style={{ fontSize: 10, color: 'var(--rh-green)', display: 'flex', alignItems: 'center', gap: 2 }}>
                            ✓ {result.bestMatch}
                          </span>
                        )}
                        {result.positions?.[0] && (
                          <span style={{ fontSize: 10, color: 'var(--rh-text-muted)' }}>
                            Pos: {result.positions[0]}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Pagination footer */}
          {totalPages > 1 && (
            <div style={{
              padding: '8px 14px',
              borderTop: '1px solid var(--rh-border)',
              background: 'var(--rh-surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
              fontSize: 12,
              color: 'var(--rh-text-muted)',
            }}>
              <button className="rh-page-btn"
                disabled={safePage === 1}
                onClick={() => setCurrentPage(p => p - 1)}>
                ‹ Prev
              </button>
              <span>Page {safePage} / {totalPages}</span>
              <button className="rh-page-btn"
                disabled={safePage >= totalPages}
                onClick={() => setCurrentPage(p => p + 1)}>
                Next ›
              </button>
            </div>
          )}
        </div>

        {/* ── Right panel — alignment viewer ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--rh-bg)' }}>

          {/* Viewer header */}
          <div style={{
            padding: '10px 16px',
            background: 'var(--rh-surface)',
            borderBottom: '1px solid var(--rh-border)',
            flexShrink: 0,
          }}>
            {selectedAlignment ? (
              <div style={{ fontSize: 12, fontFamily: 'var(--rh-mono)', color: 'var(--rh-text)' }}>
                <strong>Query:</strong> {selectedAlignment.qseqid}
                <span style={{ margin: '0 8px', color: 'var(--rh-text-faint)' }}>·</span>
                <strong>Subject:</strong> {selectedAlignment.sseqid}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--rh-text-muted)' }}>
                Select an alignment from the list to view details
              </div>
            )}
          </div>

          {/* Viewer body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {selectedAlignment ? (
              <SequenceAlignmentViz
                alignments={selectedAlignment}
                onSimilarityRangeFound={handleSimilarityRangeFound}
              />
            ) : (
              <div className="rh-empty">
                <div className="rh-empty-icon" style={{ fontSize: 40 }}>🧬</div>
                <span>Select an alignment from the list to view details</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Guide Modal */}
      <UserGuideSequenceAlignment open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  );
};

export default SequenceComponent;